import { useReducer } from "react";

export type StepStatus =
  | "start"
  | "photo-pending"
  | "photo-review"
  | "photo-rejected"
  | "questionnaire-topic"
  | "questionnaire-input"
  | "sent";

type FlowState = {
  status: StepStatus;
  furthestStatus: StepStatus;
  eletterSending: boolean;
  eletterError: string | null;
};

type FlowAction =
  | { type: "NEXT_STEP"; payload: StepStatus }
  | { type: "START_SENDING" }
  | { type: "SEND_SUCCESS" }
  | { type: "SEND_ERROR"; payload: string }
  | { type: "CLEAR_ERROR" };

// Progress order for status tracking
const STATUS_PROGRESS_ORDER: { [key in StepStatus]: number } = {
  start: 0,
  "photo-pending": 1,
  "photo-review": 2,
  "photo-rejected": 1, // Same level as photo-pending
  "questionnaire-topic": 3,
  "questionnaire-input": 4,
  sent: 5
};

function flowReducer(state: FlowState, action: FlowAction): FlowState {
  switch (action.type) {
    case "NEXT_STEP": {
      // Only update furthestStatus if the new status is further in the flow
      const currentProgress = STATUS_PROGRESS_ORDER[state.furthestStatus];
      const newProgress = STATUS_PROGRESS_ORDER[action.payload];

      return {
        ...state,
        status: action.payload,
        furthestStatus: newProgress > currentProgress ? action.payload : state.furthestStatus
      };
    }
    case "START_SENDING":
      return {
        ...state,
        eletterSending: true,
        eletterError: null
      };
    case "SEND_SUCCESS":
      return {
        ...state,
        status: "sent",
        furthestStatus: "sent", // Always update furthestStatus on success
        eletterSending: false
      };
    case "SEND_ERROR":
      return {
        ...state,
        eletterSending: false,
        eletterError: action.payload
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        eletterError: null
      };
    default:
      return state;
  }
}

export function useFormFlow(initialStatus: StepStatus = "start") {
  const [state, dispatch] = useReducer(flowReducer, {
    status: initialStatus,
    furthestStatus: initialStatus, // Initialize furthestStatus to match initialStatus
    eletterSending: false,
    eletterError: null
  });

  return {
    flowState: state,
    setStatus: (status: StepStatus) => dispatch({ type: "NEXT_STEP", payload: status }),
    startSending: () => dispatch({ type: "START_SENDING" }),
    sendSuccess: () => dispatch({ type: "SEND_SUCCESS" }),
    sendError: (message: string) => dispatch({ type: "SEND_ERROR", payload: message }),
    clearError: () => dispatch({ type: "CLEAR_ERROR" })
  };
}
